import { useEffect } from 'react';

const upsertMeta = (attr, key, content) => {
  if (content == null || content === '') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', String(content));
};

const upsertLink = (rel, href, extra = {}) => {
  if (!href) return;
  const selector = Object.entries(extra).reduce(
    (s, [k, v]) => `${s}[${k}="${v}"]`,
    `link[rel="${rel}"]`
  );
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const removeLinks = (rel, extraKey) => {
  document.head.querySelectorAll(`link[rel="${rel}"]`).forEach((el) => {
    if (!extraKey || el.hasAttribute(extraKey)) el.remove();
  });
};

/**
 * Client-side SEO head updater for the SPA.
 * Defaults in public/index.html cover first paint / non-JS crawlers.
 */
export default function SeoHead({
  title,
  description,
  path = '/',
  image = 'https://www.groupeouambo.com/og-image.png',
  lang = 'en',
  siteName = 'Groupe Ouambo',
  siteUrl = 'https://www.groupeouambo.com',
  jsonLd = null,
  noindex = false,
  type = 'website',
  alternates = null,
}) {
  useEffect(() => {
    const url = `${siteUrl.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
    const fullTitle = title;

    document.title = fullTitle;
    document.documentElement.lang = lang === 'fr' ? 'fr' : 'en';

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'theme-color', '#0B1F3A');
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');

    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:site_name', siteName);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    upsertMeta('property', 'og:locale', lang === 'fr' ? 'fr_FR' : 'en_US');
    upsertMeta('property', 'og:locale:alternate', lang === 'fr' ? 'en_US' : 'fr_FR');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);

    upsertLink('canonical', url);

    removeLinks('alternate', 'hreflang');
    if (Array.isArray(alternates) && alternates.length) {
      alternates.forEach((a) => {
        if (a && a.href && a.hreflang) upsertLink('alternate', a.href, { hreflang: a.hreflang });
      });
    } else {
      upsertLink('alternate', url, { hreflang: 'en' });
      upsertLink('alternate', url, { hreflang: 'fr' });
      upsertLink('alternate', url, { hreflang: 'x-default' });
    }

    const scriptId = 'go-jsonld';
    let script = document.getElementById(scriptId);
    if (jsonLd) {
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
  }, [title, description, path, image, lang, siteName, siteUrl, JSON.stringify(jsonLd), noindex, type, JSON.stringify(alternates)]);

  return null;
}
